/**
 * Source color/label registry + THE render predicate, shared by the
 * legacy Leaflet path (explore.tsx marker loop, LegendBody) and the
 * MapLibre tile-mode path (explore-map-tiles.ts). Moved out of
 * explore.tsx without behavior change so both map engines and the
 * legend can never disagree on color, label, or visibility rules.
 */

import { inTimeWindow, type TimeWindow } from './time-window';

// Water-first redesign: litter marks are colored by ONE OF THREE CLASSES,
// not a per-source hue. The old 8-hue provenance rainbow spent the map's
// entire color budget on where a report came from — the least important
// thing to a resident — and read as noise against the water layer.
// Provenance now lives in the popup label; the map groups litter into
// resident / city-311 / imported, all in one muted WARM family so that
// COOL water color (watershed impairment + the flow-to-intake trace)
// carries the story.
export const LITTER_CLASS = {
  citizen: '#d3c39a', // resident reports (this site + Reddit)
  official: '#b58a5c', // city 311 service requests
  imported: '#8c8474', // OpenLitterMap / news / detections / curated
} as const;
export type LitterClass = keyof typeof LITTER_CLASS;

export const LITTER_CLASS_OF: Record<string, LitterClass> = {
  reddit: 'citizen',
  user_submission: 'citizen',
  dc311: 'official',
  news: 'imported',
  olm: 'imported',
  mapillary: 'imported',
  curated: 'imported',
  other: 'imported',
};

export const LITTER_CLASS_STYLE: Record<LitterClass, { color: string; label: string; hint: string }> = {
  citizen: { color: LITTER_CLASS.citizen, label: 'Resident reports', hint: 'this site + Reddit' },
  official: { color: LITTER_CLASS.official, label: 'City 311', hint: 'municipal service requests' },
  imported: { color: LITTER_CLASS.imported, label: 'Imported data', hint: 'OpenLitterMap, news, detections' },
};

// SOURCE_STYLE keeps its shape (color + label) so every existing consumer
// is unchanged; only the COLOR now comes from the source's litter class,
// so eight sources collapse onto three muted swatches. Labels stay
// per-source because the popup still names the exact provenance.
export const SOURCE_STYLE: Record<string, { color: string; label: string }> = {
  reddit:          { color: LITTER_CLASS.citizen,  label: 'Reddit' },
  news:            { color: LITTER_CLASS.imported, label: 'News' },
  olm:             { color: LITTER_CLASS.imported, label: 'OpenLitterMap' },
  mapillary:       { color: LITTER_CLASS.imported, label: 'Street-photo detection (Mapillary)' },
  curated:         { color: LITTER_CLASS.imported, label: 'Staff-verified finding' },
  user_submission: { color: LITTER_CLASS.citizen,  label: 'User submission' },
  dc311:           { color: LITTER_CLASS.official, label: 'DC 311 report' },
  other:           { color: LITTER_CLASS.imported, label: 'Observation' },
};

export function sourceKey(p: any): keyof typeof SOURCE_STYLE {
  const t = p.source.type;
  if (t === 'user_submission') return 'user_submission';
  if (t === 'curated') return 'curated';
  if (t === 'news') return 'news';
  if (t === 'cv_detection') return 'mapillary';
  if (t === 'dc311') return 'dc311';
  const n = (p.source.source_name ?? '').toLowerCase();
  if (n.includes('reddit')) return 'reddit';
  if (n.includes('openlittermap')) return 'olm';
  if (n.includes('mapillary')) return 'mapillary';
  return 'other';
}

/**
 * Does this observation feature actually put something on the map?
 * THE single render/visibility predicate — shared by the firefly buffer
 * builder (firefly-data.ts), the legacy Leaflet marker loop, the popup's
 * "showing {K}" population, and the pill's visibleFeatures memo, so every
 * count describes exactly the records that draw a mark:
 *
 *   1. Verified filter (Q4) + the contributor's own reciprocity bypass
 *      (contract §8: their recent submissions always render).
 *   2. Time-window filter (trail #temporal-insight): `window` defaults
 *      to 'all' so every existing caller (both engines, both fixtures)
 *      is unaffected unless it explicitly opts into a narrower window.
 *      A record whose [observed_first ?? observed_at, observed_at]
 *      range doesn't intersect the window is hidden — including
 *      undated records, which only match 'all' (see time-window.ts).
 *
 * ── THE OLM TAG GATE IS GONE (firefly cartography) ────────────────────
 * Until the firefly renderer landed there was a THIRD clause here: an
 * OpenLitterMap record without `_display.profile_shares` +
 * `_display.tag_breakdown` drew nothing, because OLM records rendered
 * ONLY as painterly piles and the pile geometry was derived from that
 * tag data. That coupling is what made `profile_shares` a load-bearing
 * render gate rather than pile cruft.
 *
 * A firefly is a point of light at a coordinate: it needs a lon/lat and
 * nothing else. A count-only OLM cluster now DOES draw, so excluding it
 * from the counts would be the lie. The gate is therefore removed rather
 * than rewritten — deleting it BEFORE the pile renderer would have
 * silently dropped every un-enriched OLM row from the map and from every
 * count ("{n} in view", "showing {K}", region-count, spotlight
 * membership); deleting it after is what makes those counts honest
 * again. Quantity still modulates a firefly's radius via
 * `_display.tag_breakdown.total_items` → `quantity` → 1
 * (firefly-data.ts:itemCountOf), it just no longer gates visibility.
 */
export function rendersOnMap(
  f: { id?: unknown; properties?: unknown },
  showUnverified: boolean,
  myRecentIds: Set<string>,
  window: TimeWindow = 'all',
): boolean {
  const p = f.properties as any;
  if (!showUnverified && p?.verified === false && !myRecentIds.has(String(f.id))) {
    return false;
  }
  if (window !== 'all' && !inTimeWindow(f, window)) return false;
  return true;
}
